import React, { useState } from 'react'
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Checkbox,
  FormControlLabel,
  Stack,
} from '@mui/material'

interface HighRiskEditableDataRowProps {
  text: string
  onUpdate: (newValue: string) => Promise<boolean>
  fieldName?: string
}

const HighRiskEditableDataRow: React.FC<HighRiskEditableDataRowProps> = ({
  text,
  onUpdate,
  fieldName = 'email',
}) => {
  const [open, setOpen] = useState(false)
  const [newValue, setNewValue] = useState('')
  const [confirmValue, setConfirmValue] = useState('')
  const [error, setError] = useState('')
  const [confirmChecked, setConfirmChecked] = useState(false)
  const [verificationChecked, setVerificationChecked] = useState(false)

  const handleOpen = () => {
    setNewValue('')
    setConfirmValue('')
    setError('')
    setConfirmChecked(false)
    setVerificationChecked(false)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleUpdate = async () => {
    if (newValue !== confirmValue) {
      setError(`The ${fieldName} fields do not match.`)
      return
    }

    if (!confirmChecked || !verificationChecked) {
      setError('You must check both confirmation boxes to proceed.')
      return
    }

    if (newValue === text) {
      setError(`The new ${fieldName} is the same as the current one.`)
      return
    }

    try {
      const success = await onUpdate(newValue)

      if (success) {
        handleClose()
      } else {
        setError(`Failed to update ${fieldName}. Please try again.`)
      }
    } catch (err) {
      setError(
        `Error updating ${fieldName}: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`
      )
    } finally {
    }
  }

  return (
    <>
      <div className="option-high-risk-editable">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {text}{' '}
          <IconButton
            size="small"
            color="primary"
            onClick={handleOpen}
            aria-label="Edit email"
          >
            Edit
          </IconButton>
        </div>
      </div>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component="span" fontWeight="bold">
            Update User {fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography fontWeight="bold">
              HIGH RISK ACTION: Changing a user&apos;s {fieldName} affects their
              account access
            </Typography>
          </Alert>

          <DialogContentText paragraph>
            Updating a user&apos;s {fieldName} is a high-risk action that will
            change how they log in to the system. This could potentially give
            unauthorized access to a patient&apos;s health records.
          </DialogContentText>

          <DialogContentText paragraph color="error.main" fontWeight="medium">
            Before proceeding, you MUST verify the user&apos;s identity through
            alternative means (phone call to verified number, picture ID, etc).
          </DialogContentText>

          <Box my={3}>
            <Typography fontWeight="medium" gutterBottom>
              Current {fieldName}:{' '}
              <Box component="span" fontWeight="bold">
                {text}
              </Box>
            </Typography>

            <TextField
              fullWidth
              label={`New ${fieldName}`}
              variant="outlined"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              margin="normal"
            />

            <TextField
              fullWidth
              label={`Confirm new ${fieldName}`}
              variant="outlined"
              value={confirmValue}
              onChange={(e) => setConfirmValue(e.target.value)}
              margin="normal"
              error={!!error && newValue !== confirmValue}
              helperText={error && newValue !== confirmValue ? error : ''}
            />
          </Box>

          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={verificationChecked}
                  onChange={(e) => setVerificationChecked(e.target.checked)}
                />
              }
              label={
                <Typography>
                  I have verified the user&apos;s identity through a secure
                  secondary method
                </Typography>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                />
              }
              label={
                <Typography>
                  I understand this is a high-risk action that affects user
                  access
                </Typography>
              }
            />
          </Stack>

          {error && newValue === confirmValue && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            color="error"
            sx={{ ml: 2 }}
            disabled={
              !newValue ||
              !confirmValue ||
              !confirmChecked ||
              !verificationChecked
            }
          >
            Update Email
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default HighRiskEditableDataRow
